import React, { useState, useCallback } from "react";
import i18next from "i18next";
import Modal from "../../../StyledComponents/Modal";
import Content from "../../../StyledComponents/Content";
import {
  Repeat,
  RepeatTiny,
  RepeatSmall,
} from "../../../StyledComponents/Repeat";
import LogoBox from "../../../StyledComponents/LogoBox";
import DetailBox from "../../../StyledComponents/DetailBox";
import ExtendInfo from "../../../StyledComponents/ExtendInfo";
import Snackbar from "../../../StyledComponents/Snackbar";
import { UtilityTextBold } from "../../../StyledComponents/Utility";
import InputLabel from "../../../StyledComponents/InputLabel";
import InputText from "../../../StyledComponents/InputText";
import InputCheckbox from "../../../StyledComponents/InputCheckbox";
import InputSelect, {
  OptionProps,
} from "../../../StyledComponents/InputSelect";
import { H4 } from "../../../StyledComponents/Heading";
import {
  InputGroup,
  InputGroupColumn,
} from "../../../StyledComponents/InputGroup";
import {
  ButtonPrimary,
  ButtonContent,
  ButtonInline,
} from "../../../StyledComponents/Button";
import {
  ContentLogo,
  ContentLogoText,
  ContentLogoMedia,
} from "../../../StyledComponents/ContentLogo";
import { ColumnRow, ColumnRowItem } from "../../../StyledComponents/ColumnRow";
import useInsuranceCalculation from "../Hooks/useInsurance";
import { ssnIsValid } from "../../../utils/ssn";
import Loader from "../../../StyledComponents/Loader";
import { numberSeparator } from "../../../utils/formats";
import {
  DrivingDistance,
  InsuranceOption,
  Branch,
} from "../../../@types/codegen/types";
import PubSub from "../../../utils/PubsubItem";

interface FormData {
  ssn: string;
  drivingDistance: DrivingDistance;
}

interface InsuranceModal {
  id: string;
  branch?: Branch | null;
  onClose: () => void;
  insuranceOptions: InsuranceOption;
}

const InsuranceModal = ({
  id,
  branch,
  onClose,
  insuranceOptions,
}: InsuranceModal) => {
  const [form, setForm] = useState<FormData>({
    ssn: "",
    drivingDistance: DrivingDistance.Between0And1000,
  });
  const [payload, setPayload] = useState<FormData>();
  const { loading, data, error } = useInsuranceCalculation(
    id,
    branch?.id ? branch?.id : undefined,
    ssnIsValid(payload?.ssn) ? payload?.ssn : undefined,
    payload?.drivingDistance
  );

  const onChangeSsn = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, ssn: e.currentTarget.value }),
    [form]
  );

  const onChangeDrivingDistance = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) =>
      setForm({
        ...form,
        drivingDistance: e.currentTarget.value as DrivingDistance,
      }),
    [form]
  );

  const onShowInsurances = useCallback(() => {
    PubSub.publish("InsuranceInterest");
    setPayload(form);
  }, [form]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.keyCode === 13 && ssnIsValid(form.ssn) && !loading) {
        onShowInsurances();
      }
    },
    [form]
  );

  const options: OptionProps[] = [
    { value: DrivingDistance.Between0And1000, displayName: "0 - 1000" },
    { value: DrivingDistance.Between1000And1500, displayName: "1000 - 1500" },
    { value: DrivingDistance.Between1500And2000, displayName: "1500 - 2000" },
    { value: DrivingDistance.Between2000And2500, displayName: "2000 - 2500" },
    { value: DrivingDistance.Over2500, displayName: "2500+" },
  ];

  return (
    <Modal title={i18next.t("item.insurance")} onClose={onClose}>
      {(insuranceOptions?.description || insuranceOptions?.logotype) && (
        <Repeat>
          {insuranceOptions.description ? (
            <ContentLogo>
              <ContentLogoText>
                <Content>
                  <p>{insuranceOptions.description}</p>
                </Content>
              </ContentLogoText>
              {insuranceOptions.logotype && (
                <ContentLogoMedia>
                  <LogoBox
                    logo={insuranceOptions.logotype}
                    alt={
                      insuranceOptions.name ||
                      i18next.t("common.logotype") ||
                      ""
                    }
                    wide
                  />
                </ContentLogoMedia>
              )}
            </ContentLogo>
          ) : (
            <>
              {insuranceOptions.logotype && (
                <LogoBox
                  logo={insuranceOptions.logotype}
                  alt={
                    insuranceOptions.name || i18next.t("common.logotype") || ""
                  }
                  wide
                />
              )}
            </>
          )}
        </Repeat>
      )}

      <Repeat>
        <RepeatSmall>
          <RepeatTiny>
            <InputGroup>
              <InputGroupColumn>
                <InputLabel htmlFor="input-insurance-personalnumber">
                  {i18next.t("item.personalNumber")}
                </InputLabel>
                <InputText
                  placeholder="ÅÅÅÅMMDD-XXXX"
                  label={i18next.t("item.personalNumber")}
                  value={form.ssn}
                  onChange={onChangeSsn}
                  onKeyDown={onKeyDown}
                  id="input-insurance-personalnumber"
                />
              </InputGroupColumn>
              <InputGroupColumn>
                <InputLabel htmlFor="input-insurance-mileage">
                  {i18next.t("item.estimatedAnualMileage")}
                </InputLabel>
                <InputSelect
                  value={form.drivingDistance}
                  onChange={onChangeDrivingDistance}
                  options={options}
                  unit="mil"
                  title={i18next.t("item.estimatedAnualMileage")}
                />
              </InputGroupColumn>
            </InputGroup>
          </RepeatTiny>
          <RepeatTiny>
            <ButtonPrimary
              fullWidth
              disabled={!ssnIsValid(form.ssn) || loading}
              onClick={onShowInsurances}
            >
              <ButtonContent>{i18next.t("item.showInsurances")}</ButtonContent>
            </ButtonPrimary>
          </RepeatTiny>
        </RepeatSmall>
        {false && (
          <RepeatSmall>
            <InputCheckbox id="input-insurance-save-personalnumber">
              {i18next.t("item.savePersonalNumberOnThisDevice")}
            </InputCheckbox>
          </RepeatSmall>
        )}
        <RepeatSmall>
          <Content small>
            <p>{i18next.t("item.savePersonalNumberOnThisDeviceHelp")}</p>
          </Content>
        </RepeatSmall>
      </Repeat>

      {loading && (
        <Repeat>
          <Loader />
        </Repeat>
      )}

      {error && (
        <Repeat>
          <Snackbar
            severity="error"
            icon
            heading={i18next.t("common.anErrorHasOccured") || undefined}
          >
            {i18next.t("item.couldNotfetchInsurances")}
          </Snackbar>
        </Repeat>
      )}

      <>
        {data?.insurances && (
          <Repeat>
            {data?.insurances.map((insurance) => (
              <Repeat key={insurance.name}>
                <DetailBox
                  heading={`${numberSeparator(insurance.price)} ${
                    insurance.unit
                  }`}
                  description={insurance.name}
                >
                  <Repeat>
                    <>
                      {insurance.description && (
                        <RepeatTiny>
                          <Content>
                            <p>{insurance.description}</p>
                          </Content>
                        </RepeatTiny>
                      )}
                    </>
                    <>
                      {insurance.url && (
                        <RepeatTiny>
                          <ButtonInline
                            as="a"
                            href={insurance.url || ""}
                            title={
                              i18next.t(
                                "item.openPrePurchaseInformationInNewTab"
                              ) || ""
                            }
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                          >
                            {i18next.t(
                              "item.openPrePurchaseInformationInNewTab"
                            )}
                          </ButtonInline>
                        </RepeatTiny>
                      )}
                    </>
                  </Repeat>
                  {insurance.items && insurance.items.length > 0 && (
                    <Repeat>
                      <H4>{i18next.t("item.insuranceIncludes")}</H4>
                      {insurance.items.map((item) => (
                        <RepeatSmall key={item.name || ""}>
                          <ExtendInfo title={item.name || ""}>
                            {item.description}
                          </ExtendInfo>
                        </RepeatSmall>
                      ))}
                    </Repeat>
                  )}
                  {insurance.addons && insurance.addons.length > 0 && (
                    <Repeat>
                      <H4>{i18next.t("item.addInsuranceHeading")}</H4>
                      {insurance.addons.map((addon) => (
                        <RepeatSmall key={addon.title || ""}>
                          <ColumnRow>
                            <ColumnRowItem>
                              <ExtendInfo title={addon.title || ""}>
                                {addon.description}
                              </ExtendInfo>
                            </ColumnRowItem>
                            <ColumnRowItem>
                              <UtilityTextBold>
                                +{addon.price} {addon.unit}
                              </UtilityTextBold>
                            </ColumnRowItem>
                          </ColumnRow>
                        </RepeatSmall>
                      ))}
                    </Repeat>
                  )}
                </DetailBox>
              </Repeat>
            ))}
          </Repeat>
        )}
      </>
    </Modal>
  );
};

export default InsuranceModal;
